/**
 * Hooks para a tela de curadoria de artes /admin/arts/review (TH-55167).
 *
 * Endpoints backend (TH-55163):
 *   GET  /custom-arts/pending?scope=global|supplier&page&per_page
 *   POST /custom-arts/:id/approve  body: { notes? }
 *   POST /custom-arts/:id/reject   body: { notes }  (notes obrigatório)
 *
 * Status válidos no backend: draft | pending_review | active | rejected | archived.
 * A fila lista apenas pending_review.
 *
 * - super_admin: vê scope global + supplier.
 * - catalog_operator: backend força scope='supplier' (independente do filtro UI).
 *
 * Mantemos invalidações co-localizadas com o contrato HTTP (mesmo padrão de
 * useCustomArts.ts). Toda mutation invalida ["arts-review", "pending"] e
 * ["custom-arts"] para consistência da galeria principal.
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import type { CustomArtRead } from "@/hooks/useCreateCustomArt";

export interface ArtReviewListMeta {
  page: number;
  per_page: number;
  total: number;
}

export interface ArtReviewListResponse {
  data: CustomArtRead[];
  meta: ArtReviewListMeta;
}

export type ArtReviewScopeFilter = "all" | "global" | "supplier";

export interface PendingArtsFilters {
  scope?: ArtReviewScopeFilter;
  page?: number;
  per_page?: number;
}

function buildQuery(filters: PendingArtsFilters): string {
  const sp = new URLSearchParams();
  if (filters.scope && filters.scope !== "all") sp.set("scope", filters.scope);
  sp.set("page", String(filters.page ?? 1));
  sp.set("per_page", String(filters.per_page ?? 20));
  return sp.toString();
}

async function fetchPendingArts(
  filters: PendingArtsFilters,
): Promise<ArtReviewListResponse> {
  const res = await fetch(`/api/custom-arts/pending?${buildQuery(filters)}`, {
    credentials: "include",
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { detail?: string };
    throw new Error(body.detail ?? `HTTP ${res.status}`);
  }
  return (await res.json()) as ArtReviewListResponse;
}

export function usePendingArts(
  filters: PendingArtsFilters,
): UseQueryResult<ArtReviewListResponse, Error> {
  return useQuery({
    queryKey: ["arts-review", "pending", filters],
    queryFn: () => fetchPendingArts(filters),
    staleTime: 15_000,
  });
}

export interface ApproveArtInput {
  id: string;
  notes?: string;
}

async function postApprove(input: ApproveArtInput): Promise<CustomArtRead> {
  const res = await fetch(`/api/custom-arts/${input.id}/approve`, {
    method: "POST",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ notes: input.notes ?? null }),
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { detail?: string };
    throw new Error(body.detail ?? `HTTP ${res.status}`);
  }
  return (await res.json()) as CustomArtRead;
}

export function useApproveArt(): UseMutationResult<
  CustomArtRead,
  Error,
  ApproveArtInput
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: postApprove,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["arts-review"] });
      void qc.invalidateQueries({ queryKey: ["custom-arts"] });
      void qc.invalidateQueries({ queryKey: ["supplier-arts"] });
    },
  });
}

export interface RejectArtInput {
  id: string;
  /** Motivo da rejeição — obrigatório; backend retorna 422 se vazio. */
  notes: string;
}

async function postReject(input: RejectArtInput): Promise<CustomArtRead> {
  const res = await fetch(`/api/custom-arts/${input.id}/reject`, {
    method: "POST",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ notes: input.notes }),
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { detail?: string };
    throw new Error(body.detail ?? `HTTP ${res.status}`);
  }
  return (await res.json()) as CustomArtRead;
}

export function useRejectArt(): UseMutationResult<
  CustomArtRead,
  Error,
  RejectArtInput
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: postReject,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["arts-review"] });
      void qc.invalidateQueries({ queryKey: ["custom-arts"] });
      void qc.invalidateQueries({ queryKey: ["supplier-arts"] });
    },
  });
}
